import z from "zod";
// region COMPONENT LIBRARY TYPES

// ---------------------
// DROPDOWN MENU

export const DropDownMenuItemSchema = z.object({
  label: z.string(),
  onClick: z.custom<() => void>().optional(),
  subItems: z
    .array(
      z.object({
        label: z.string(),
        onClick: z.custom<() => void>().optional(),
      })
    )
    .optional(),
});

export type DropDownMenuItem = z.infer<typeof DropDownMenuItemSchema>;

// ---------------------
// MULTIPLE BAR GRAPH

// Individual Items
export const multipleBarGraphItemSchema = z.object({
  label: z.string(),
  dataVals: z.array(z.object({ key: z.string(), value: z.number() })),
});

export type MultipleBarGraphItem = z.infer<typeof multipleBarGraphItemSchema>;

// Full Bar Graph
export const MultipleBarGraphSchema = z.object({
  title: z.string().optional(),
  items: z.array(multipleBarGraphItemSchema),
});

export type MultipleBarGraph = z.infer<typeof MultipleBarGraphSchema>;

// endregion COMPONENT LIBRARY TYPES

export type SocketCom = {
  command:
    | "auth"
    | "magicLink"
    | "login"
    | "refreshToken"
    | "userRegister"
    | "getUsersList"
    | "roleUpdate"
    | "userOrders"
    | "orderByID"
    | "userQuotes"
    | "quoteByID"
    | "quoteApproval"
    | "userProfileGet"
    | "userProfileUpdate"
    | "userDashboard"
    | "orderDuplicate"
    | "getAdminList"
    | "updateOrderBranch"
    | "getAllUsersWithCompanies"
    | "adminCompaniesMerge"
    | "getUserDetails"
    | "updateUserDetails";
  data: unknown;
  // http status code used for incoming messages
  status?: number;
};

export const NewAlertSchema = z.object({
  // TODO: Change per client requirements
  id: z.number(),
  timestamp: z.number(),
  alertLevel: z.enum(["info", "warning", "error"]),
  alertType: z.enum(["clientRequirement"]),
});

export type NewAlert = z.infer<typeof NewAlertSchema>;

export const AUTH_METHODS = [
  "Google",
  "Office 365",
  "Facebook",
  "LinkedIn",
] as const;

const USER_COMMUNICATION_METHOD = ["email", "sms"] as const;

export const companyAddressSchema = z.object({
  addressLine1: z.string(),
  addressLine2: z.string().optional(),
  suburb: z.string(),
  town: z.string(),
  postalCode: z.string(),
});

export type CompanyAddress = z.infer<typeof companyAddressSchema>;

export const addressCompanySchema = z.object({
  companyName: z.string().optional(),
  address: companyAddressSchema,
});

export type AddressCompanyType = z.infer<typeof addressCompanySchema>;

export const registerUserSchema = z.object({
  fullName: z.string(),
  email: z.string(),
  password: z.string(),
  mobileNumber: z.string().optional(),
  // authMethod: z.enum(AUTH_METHODS),
});

export type RegisterUser = z.infer<typeof registerUserSchema>;

// region DASHBOARD TYPES

/**
 * Editable Data type
 */
export const columnKeySetSchema = z.object({
  accessorKey: z.string(),
  displayName: z.string(),
  enableSorting: z.boolean(),
  enableHiding: z.boolean(),
  enableEditing: z.boolean(),
});

export type ColumnKeySet = z.infer<typeof columnKeySetSchema>;

//! Dash Main Table Test Data
export const dashMainDataSchema = z.object({
  primaryKey: z.string(),
  orderNumber: z.string(),
  clientName: z.string(),
  product: z.string(),
  quantity: z.number(),
  orderValue: z.number(),
  status: z.string(),
  deliveryDate: z.string(),
  priority: z.string(),
  warehouseLocation: z.string(),
  accountManager: z.string(),
});

export type DashMainDataType = z.infer<typeof dashMainDataSchema>;

//! Test Data type
export const dashJobDataSchema = z.object({
  OrderNumber: z.string(),
  PartName: z.string(),
  Length: z.number(),
  Width: z.number(),
  MaterialGrade: z.number(),
  Thick: z.number(),
  Weight: z.number(),
  Qty: z.number(),
  Nest: z.number(),
  Cut: z.number(),
  Bend: z.number(),
  Sort: z.number(),
  DNIssued: z.number(),
  DNPOD: z.number(),
  ToIssue: z.number(),
  DrawingNumber: z.number(),
  NCNumber: z.number(),
  Query: z.number(),
  doubleClick: z.custom<() => void>(),
});

export type DashJobData = z.infer<typeof dashJobDataSchema>;

export const dashJobDataKeySetSchema = z.object({
  key: z.string(),
  data: z.array(dashJobDataSchema),
});

export type DashJobDataKeySet = z.infer<typeof dashJobDataKeySetSchema>;

export const dashJobDataKeySetArraySchema = z.array(dashJobDataKeySetSchema);

export type DashJobDataKeySetArray = z.infer<
  typeof dashJobDataKeySetArraySchema
>;

// endRegion DASHBOARD TYPES

export type SendRegisterUserBranch = {
  name: string;
  city: string;
  latitude: string;
  longitude: string;
  address1: string;
  pinCode: string;
  suburb: string | undefined;
  address2: string | undefined;
  province: string;
  country: string;
  officeParkName: string | undefined;
};

const COMPANY_TYPE = ["business", "private"] as const;

export type SendRegisterUserCompany = {
  name: string;
  industryType: string;
  phoneNumber: string;
  registrationNumber: string | undefined;
  taxNumber: string | undefined;
  branches: SendRegisterUserBranch[];
  type: (typeof COMPANY_TYPE)[number];
};

export type SendRegisterUser = {
  email: string;
  password: string;
  fullName: string;
  designation: string;
  phoneNumber: string;
  title: string | undefined;
  roleID: string | undefined;
  companies: SendRegisterUserCompany[];
  preferredLanguage: string;
  preferredCommunicationMethods: Array<
    (typeof USER_COMMUNICATION_METHOD)[number]
  >;
  marketingOptIn: boolean;
};

export const ROLE_NAMES = [
  "super_admin",
  "admin",
  "new_user",
  "approved_user",
  "blocked_user",
] as const;

export const roleNameSchema = z.enum(ROLE_NAMES);
export type RoleName = z.infer<typeof roleNameSchema>;

export const dbUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  phoneNumber: z.string(),
  roleID: z.string(),
  lastLogin: z.string().nullable(),
  roleName: roleNameSchema,
  designation: z.string(),
  preferredLanguage: z.string(),
  preferredCommunicationMethods: z.array(z.enum(USER_COMMUNICATION_METHOD)),
  marketingOptIn: z.boolean(),
  note: z.string().nullish(),
});

export type UserType = z.infer<typeof dbUserSchema>;

const ORDER_STATUS = ["in_progress", "completed"] as const;

export const simpleOrderSchema = z.object({
  id: z.string(),
  branch_id: z.string(),
  quote_id: z.string().nullable(),
  quoted_by: z.string(),
  file_id: z.string().nullable(),
  vsb_job_number: z.string(),
  wo_number: z.string(),
  quote_number: z.string().nullable(),
  customer_order_number: z.string(),
  reference: z.string().nullable(),
  approval_at: z.coerce.date().nullable(),
  completed_at: z.coerce.date(),
  shipped: z.enum(["Y", "N"]),
  note: z.string().nullable(),
  quoted_by_name: z.string(),
  invoice_number: z.string().nullable(),
  delivery_note_issued: z.enum(["Y", "N"]),
  delivery_note_proof_of_delivery: z.enum(["Y", "N"]),
  shipment_method: z.enum(["Collect", "Deliver"]),
  status: z.enum(ORDER_STATUS),
  user_name: z.string(),
  job_progress: z.number().int().min(0).max(100),
  company_name: z.string(),
  account_number: z.string().optional(),
});

export type SimpleOrder = z.infer<typeof simpleOrderSchema>;

export const orderItem = z.object({
  id: z.string(), // order_item.id, BIGINT
  order_id: z.string(), // order_item.order_id, BIGINT
  // item_file_id: z.string().nullable(), // order_item.file_id, BIGINT or NULL
  part_no: z.string(),
  length: z.string(),
  width: z.string(),
  material_grade: z.string(),
  thickness: z.string(),
  weight: z.string(),
  qty: z.number().int(),
  cut: z.enum(["Y", "N"]),
  bend: z.enum(["Y", "N"]),
  shot: z.enum(["Y", "N"]),
  created_at: z.coerce.date(), // Created at as a date
  item_file_drawing_no: z.string().nullable(), // Can be NULL
  item_file_meta: z.string().nullable(), // Allows any JSON object, or use specific shape if known
});

export type OrderItem = z.infer<typeof orderItem>;

export const fullSingleOrderSchema = z.object({
  id: z.string(), // orders.id, BIGINT
  branch_id: z.string(), // orders.branch_id, BIGINT
  quote_id: z.string().nullable(), // orders.quote_id, BIGINT or NULL
  created_by_user_id: z.string(), // orders.user_id, BIGINT
  quoted_by: z.string(), // orders.quoted_by, BIGINT
  file_id: z.string().nullable(), // orders.file_id, BIGINT or NULL
  order_items: z.array(orderItem), // Array of order items
  nc_number: z.string().nullable(),
  quoted_by_name: z.string(),
  file_drawing_no: z.string().nullable(),
  file_meta: z.string().nullable(),
  delivery_note_issued: z.enum(["Y", "N"]),
  delivery_note_proof_of_delivery: z.enum(["Y", "N"]),
  assigned_to: z.string().nullable(),
});

export type FullSingleOrder = z.infer<typeof fullSingleOrderSchema>;

export type ConvertedOrderItem = OrderItem & {
  delivery_note_issued: FullSingleOrder["delivery_note_issued"];
  delivery_note_proof_of_delivery: FullSingleOrder["delivery_note_proof_of_delivery"];
  // file_drawing_no: FullSingleOrder["file_drawing_no"];
  nc_number: FullSingleOrder["nc_number"];
  assigned_to: FullSingleOrder["assigned_to"];
};

export const QUOTE_STATUS = [
  "draft",
  "rejected",
  "expired",
  "open",
  "accepted",
] as const;

export const simpleQuotationSchema = z.object({
  id: z.string(), // quotes.id (quote id as string, BIGINT)
  branch_id: z.string(), // quotes.branch_id (BIGINT)
  created_by_user_id: z.string(), // quotes.user_id (customer, BIGINT)
  quoted_by: z.string(), // quotes.quoted_by (BIGINT)
  file_id: z.string().nullable(), // quotes.file_id (BIGINT, can be NULL)

  quote_number: z.string(),
  vds_job_number: z.string(),
  customer_reference: z.string().nullable(), // Can be NULL
  status: z.enum(QUOTE_STATUS),
  line_no: z.number().int(),
  vat_amount: z.number().int(),
  total_excl_vat: z.string(),
  total_incl_vat: z.string(),
  quote_created_at: z.coerce.date(),
  quote_updated_at: z.coerce.date(),
  quote_expiry_at: z.coerce.date().nullable(),
  quote_approval_at: z.coerce.date().nullable(),

  company_name: z.string(),
  account_number: z.string().optional(),
  user_name: z.string(),
  quoted_by_name: z.string(),
  note: z.string().nullable(),
});

export type SimpleQuotation = z.infer<typeof simpleQuotationSchema>;

export const quoteItemsSchema = z.object({
  id: z.string(), // order_item.id, BIGINT
  quote_id: z.string(), // order_item.quote_id, BIGINT
  name: z.string(),
  description: z.string().nullable(),
  material_type: z.string(),
  process_type: z.string(),
  bending_tolerance: z.string().nullable(),
  thickness: z.string(),
  width: z.string(),
  length: z.string(),
  qty: z.number().int(),
  price_per_item: z.string(),
  total_price_per_item: z.string(),
  item_created_at: z.coerce.date(), // Created at as a date
  item_file_meta: z.string().nullable(), // URL to the image, can be NULL
  item_file_drawing_no: z.string().nullable(), // Can be NULL
});
export type QuoteItem = z.infer<typeof quoteItemsSchema>;

export const fullSingleQuoteSchema = z.object({
  id: z.string(), // quotes.id (quote id as string, BIGINT)
  branch_id: z.string(), // quotes.branch_id (BIGINT)
  created_by_user_id: z.string(), // quotes.user_id (customer, BIGINT)
  quoted_by: z.string(), // quotes.quoted_by (BIGINT)
  file_id: z.string().nullable(), // quotes.file_id (BIGINT, can be NULL)

  quote_items: z.array(quoteItemsSchema), // Array of order items
  quote_number: z.string(),
  vds_job_number: z.string(),
  customer_reference: z.string().nullable(), // Can be NULL
  quote_status: z.enum(QUOTE_STATUS),
  line_no: z.number().int(),
  vat_amount: z.number().int(),
  total_excl_vat: z.string(),
  total_incl_vat: z.string(),
  quote_created_at: z.coerce.date(),
  quote_updated_at: z.coerce.date(),
  quote_expiry_at: z.coerce.date().nullable(),
  quote_approval_at: z.coerce.date().nullable(),

  // Joined/aliased columns:
  company_name: z.string(),
  branch_name: z.string(),
  branch_company_id: z.string(), // Company ID as a string (MySQL BIGINT)
  branch_address1: z.string(),
  branch_address2: z.string().nullable(),
  branch_suburb: z.string(),
  branch_city: z.string(),
  branch_province: z.string().nullable(),
  branch_pin_code: z.string().nullable(),
  user_name: z.string().nullable(),
  quoted_by_email: z.string(),
  quoted_by_phone_number: z.string(),
  quoted_by_name: z.string(),
  company_phone_number: z.string().optional(),
  file_drawing_no: z.string().nullable(), // Can be NULL
  file_meta: z.string().nullable(),
});
export type FullSingleQuote = z.infer<typeof fullSingleQuoteSchema>;

export type ConvertedQuoteItem = QuoteItem & {
  // file_drawing_no: FullSingleQuote["file_drawing_no"];
  line_no: FullSingleQuote["line_no"];
};

export const dbBranchesByCompanyIDSchema = z.object({
  company_id: z.string(),
  company_type: z.enum(COMPANY_TYPE),
  company_name: z.string(),
  registration_number: z.string().nullable(),
  tax_number: z.string().nullable(),
  industry_type: z.string(),
  phone_number: z.string(),
  account_number: z.string().optional(),
  // company_created_at: z.coerce.date(),
  // company_updated_at: z.coerce.date(),
  branches: z.array(
    z.object({
      branch_id: z.string(),
      branch_name: z.string(),
      branch_address1: z.string(),
      branch_address2: z.string().nullable(),
      branch_suburb: z.string().nullable(),
      branch_city: z.string(),
      branch_province: z.string().nullable(),
      branch_country: z.string(),
      branch_office_park_name: z.string().nullable(),
      branch_pin_code: z.string(),
      branch_latitude: z.string(),
      branch_longitude: z.string(),
      // branch_created_at: z.coerce.date(),
    })
  ),
});
export type DBBranchesByCompanyID = z.infer<typeof dbBranchesByCompanyIDSchema>;

export const userProfileDetailsSchema = z.object({
  id: z.string().min(1, "User ID is required"),
  email: z.string().email("Invalid email format"),
  title: z.string().nullable(),
  fullName: z.string(),
  designation: z.string(),
  phoneNumber: z.string(),
  preferredLanguage: z.string(),
  preferredCommunicationMethods: z.array(z.enum(USER_COMMUNICATION_METHOD)),
  marketingOptIn: z.boolean(),
  lastLogin: z.coerce.date().nullable(),
});
export type UserProfileDetails = z.infer<typeof userProfileDetailsSchema>;

export const userProfileResponseSchema = userProfileDetailsSchema.extend({
  companies: z.array(dbBranchesByCompanyIDSchema),
});
export type UserProfileResponse = z.infer<typeof userProfileResponseSchema>;

export type EditableCompany = {
  id: string;
  accountNo: string | null;
  type: (typeof COMPANY_TYPE)[number];
  name: string;
  registrationNo?: string;
  vatNo?: string;
  industryType: string;
  contactNumber: string;
  branches: EditableBranch[];
};

export type EditableBranch = {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  officeParkName?: string;
  suburb?: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  latitude: number;
  longitude: number;
};

export const dashboardCountOrderDataSchema = z.object({
  inProgress: z.number(),
  completed: z.number(),
  total: z.number(),
});
export type DashboardCountOrderData = z.infer<
  typeof dashboardCountOrderDataSchema
>;

export const dashboardCountQuoteDataSchema = z.object({
  open: z.number(),
  accepted: z.number(),
  rejected: z.number(),
  total: z.number(),
});
export type DashboardCountQuoteData = z.infer<
  typeof dashboardCountQuoteDataSchema
>;

export const dashboardCountResponseSchema = z.object({
  orderData: dashboardCountOrderDataSchema,
  quoteData: dashboardCountQuoteDataSchema,
});

export type DashboardCountResponse = z.infer<
  typeof dashboardCountResponseSchema
>;

export const authTokenDetailsSchema = z.object({
  email: z.string(),
  role: roleNameSchema,
});
export type AuthTokenDetails = z.infer<typeof authTokenDetailsSchema>;

export const orderBranchesSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type OrderBranches = z.infer<typeof orderBranchesSchema>;

export const singleOrderRequestResponseSchema = z.object({
  branches: z.array(orderBranchesSchema),
  order: fullSingleOrderSchema,
});

export type SingleOrderRequestResponse = z.infer<
  typeof singleOrderRequestResponseSchema
>;

export const dbAdminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  phoneNumber: z.string(),
  lastLogin: z.string().nullable(),
  roleName: roleNameSchema,
  designation: z.string(),
  preferredLanguage: z.string(),
});

export type AdminUserType = z.infer<typeof dbAdminUserSchema>;

export const userWithCompaniesIDsSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  email: z.string(),
  company_ids: z.array(z.string()).nullable(),
  created_at: z.coerce.date(),
});

export type UserWithCompaniesIDs = z.infer<typeof userWithCompaniesIDsSchema>;

export const responseUsersAndCompaniesSchema = z.object({
  users: z.array(userWithCompaniesIDsSchema),
  companies: z.array(dbBranchesByCompanyIDSchema),
});

export type ResponseUsersAndCompanies = z.infer<
  typeof responseUsersAndCompaniesSchema
>;

export const userAdminUpdateProfileDetailsSchema = z.object({
  id: z.string(),
  email: z.string(),
  title: z.string().nullable(),
  fullName: z.string(),
  phone_number: z.string(),
  preferredLanguage: z.string(),
  preferredCommunicationMethods: z.array(z.enum(USER_COMMUNICATION_METHOD)),
  designation: z.string(),
  marketingOptIn: z.boolean(),
  companies: z.array(dbBranchesByCompanyIDSchema),
});

export type UserAdminUpdateProfileDetails = z.infer<
  typeof userAdminUpdateProfileDetailsSchema
>;
